import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Modal from './Modal';
import { QuickAddCategoryModal, QuickAddSKUModal } from './QuickAddModal';

/**
 * Line items table with modal-based Add/Edit and read-only display.
 *
 * @param {Object} props
 * @param {Array} props.lineItems - Array of line item objects
 * @param {Function} props.setLineItems - State setter for line items
 * @param {Array} props.categories - Available categories for dropdown
 * @param {string|number} [props.vendorId] - Current vendor ID for SKU lookup
 * @param {string} [props.vendorName] - Current vendor name for display
 * @param {Function} [props.onCategoryCreated] - Callback when a new category is created
 * @param {boolean} [props.showSku=true] - Whether to show the SKU column
 */
function LineItemsTable({ lineItems, setLineItems, categories, vendorId, vendorName, onCategoryCreated, showSku = true, miscCharges = [], setMiscCharges }) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSKUModal, setShowSKUModal] = useState(false);
  const [activeLineItemId, setActiveLineItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formItem, setFormItem] = useState({ sku: '', item_name: '', category_id: '', quantity: 1, unit_of_measure: '', unit_price: 0 });
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [chargeForm, setChargeForm] = useState({ description: '', amount: 0 });

  const resetForm = () => {
    setFormItem({ sku: '', item_name: '', category_id: '', quantity: 1, unit_of_measure: '', unit_price: 0 });
  };

  const handleAdd = () => {
    const newId = lineItems.length > 0 ? Math.max(...lineItems.map(item => item.id)) + 1 : 0;
    setLineItems([...lineItems, { id: newId, ...formItem }]);
    resetForm();
    setShowAddModal(false);
  };

  const handleEditSave = () => {
    if (!editingItem) return;
    setLineItems(lineItems.map(item =>
      item.id === editingItem.id ? { ...item, ...formItem } : item
    ));
    setEditingItem(null);
    resetForm();
  };

  const openEdit = (item) => {
    setFormItem({
      sku: item.sku || '',
      item_name: item.item_name || '',
      category_id: item.category_id || '',
      quantity: item.quantity ?? 1,
      unit_of_measure: item.unit_of_measure || '',
      unit_price: item.unit_price ?? 0,
    });
    setEditingItem(item);
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const handleSKULookup = async (sku) => {
    if (!sku || !vendorId) return;
    try {
      const response = await api.getSKUCatalog({ vendor_id: vendorId, sku });
      if (response.data.length > 0) {
        const skuData = response.data[0];
        setFormItem(prev => ({
          ...prev,
          item_name: skuData.item_name,
          category_id: skuData.category_id || '',
          unit_of_measure: skuData.default_uom || '',
        }));
      }
    } catch (error) {
      console.error('Error looking up SKU:', error);
    }
  };

  const handleCategoryChange = (value) => {
    if (value === '__add_new__') {
      setShowCategoryModal(true);
    } else {
      setFormItem(prev => ({ ...prev, category_id: value }));
    }
  };

  const handleCategoryCreated = (newCategory) => {
    if (onCategoryCreated) onCategoryCreated();
    if (newCategory.id) {
      setFormItem(prev => ({ ...prev, category_id: newCategory.id }));
    }
  };

  const handleSKUCreated = (newSKU) => {
    setFormItem(prev => ({
      ...prev,
      sku: newSKU.sku,
      item_name: newSKU.item_name,
      category_id: newSKU.category_id || '',
      unit_of_measure: newSKU.default_uom || '',
    }));
    setActiveLineItemId(null);
  };

  const openSKUModal = () => {
    if (!vendorId) {
      alert('Please select a vendor first before adding SKUs to the catalog.');
      return;
    }
    setShowSKUModal(true);
  };

  const calculateLineTotal = (quantity, unitPrice) => {
    return ((parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0)).toFixed(2);
  };

  /**
   * Render the line item form fields (shared between Add and Edit modals).
   *
   * @param {string} idPrefix - Prefix for field IDs
   * @returns {JSX.Element}
   */
  const renderFormFields = (idPrefix) => (
    <>
      {showSku && (
        <div className="field">
          <label className="label" htmlFor={`${idPrefix}-sku`}>SKU</label>
          <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
            <input
              id={`${idPrefix}-sku`}
              className="input"
              type="text"
              value={formItem.sku}
              onChange={(e) => setFormItem({ ...formItem, sku: e.target.value })}
              onBlur={(e) => handleSKULookup(e.target.value)}
              placeholder="Enter SKU"
              style={{ flex: 1, fontFamily: 'var(--font-m)' }}
            />
            <button type="button" className="btn btn-ghost btn-sm" onClick={openSKUModal} title="Add new SKU to catalog">+</button>
          </div>
        </div>
      )}
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-item-name`}>Item Name <span className="req">*</span></label>
        <input
          id={`${idPrefix}-item-name`}
          className="input"
          type="text"
          value={formItem.item_name}
          onChange={(e) => setFormItem({ ...formItem, item_name: e.target.value })}
          required
          placeholder="Enter item name"
        />
      </div>
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-category`}>Category</label>
        <select
          id={`${idPrefix}-category`}
          className="input"
          value={formItem.category_id}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          <option value="">Select category...</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.full_path}</option>
          ))}
          <option value="__add_new__">+ Add New Category...</option>
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp3)' }}>
        <div className="field">
          <label className="label" htmlFor={`${idPrefix}-qty`}>Qty <span className="req">*</span></label>
          <input
            id={`${idPrefix}-qty`}
            className="input"
            type="number"
            step="0.01"
            min="0"
            value={formItem.quantity}
            onChange={(e) => setFormItem({ ...formItem, quantity: e.target.value })}
            required
            style={{ fontFamily: 'var(--font-m)' }}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`${idPrefix}-uom`}>UOM</label>
          <select
            id={`${idPrefix}-uom`}
            className="input"
            value={formItem.unit_of_measure}
            onChange={(e) => setFormItem({ ...formItem, unit_of_measure: e.target.value })}
          >
            <option value="">-</option>
            <option value="each">Each</option>
            <option value="case">Case</option>
            <option value="box">Box</option>
            <option value="pallet">Pallet</option>
            <option value="lb">Pound</option>
            <option value="kg">Kilogram</option>
            <option value="gal">Gallon</option>
            <option value="liter">Liter</option>
            <option value="dozen">Dozen</option>
            <option value="carton">Carton</option>
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor={`${idPrefix}-price`}>Unit Price <span className="req">*</span></label>
          <input
            id={`${idPrefix}-price`}
            className="input"
            type="number"
            step="0.01"
            min="0"
            value={formItem.unit_price}
            onChange={(e) => setFormItem({ ...formItem, unit_price: e.target.value })}
            required
            style={{ fontFamily: 'var(--font-m)' }}
          />
        </div>
      </div>
      <div style={{ textAlign: 'right', marginTop: 'var(--sp2)', fontSize: '0.9rem' }}>
        <span style={{ color: 'var(--st500)' }}>Line Total: </span>
        <span className="td-amt" style={{ fontWeight: 600 }}>${calculateLineTotal(formItem.quantity, formItem.unit_price)}</span>
      </div>
    </>
  );

  return (
    <div>
      {/* Table header */}
      <div className="settings-form__table-header">
        <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          Line Items ({lineItems.length}){miscCharges.length > 0 ? ` + ${miscCharges.length} charge${miscCharges.length !== 1 ? 's' : ''}` : ''}
        </h3>
        <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowAddModal(true); }}>
            + Add Item
          </button>
          {setMiscCharges && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setChargeForm({ description: '', amount: 0 }); setShowAddChargeModal(true); }}>
              + Add Charge
            </button>
          )}
        </div>
      </div>

      {/* Read-only table */}
      {lineItems.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No line items</div>
          <div className="empty-state__description">Add items to this invoice.</div>
        </div>
      ) : (
        <div className="tbl-wrap tbl-wrap--md">
          <table>
            <thead>
              <tr>
                <th scope="col">#</th>
                {showSku && <th scope="col">SKU</th>}
                <th scope="col">Item</th>
                <th scope="col">Qty</th>
                <th scope="col">UOM</th>
                <th scope="col">Price</th>
                <th scope="col">Total</th>
                <th scope="col">Category</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => {
                const calculated = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                const actual = item.actual_total != null ? item.actual_total : null;
                const hasDiscrepancy = actual != null && Math.abs(actual - calculated) > 0.01;
                const displayTotal = hasDiscrepancy ? actual : calculated;
                const catLabel = item.category_id
                  ? categories.find(c => String(c.id) === String(item.category_id))?.full_path || '—'
                  : '—';

                return (
                  <tr key={item.id}>
                    <td>{idx + 1}</td>
                    {showSku && <td style={{ fontFamily: 'var(--font-m)' }}>{item.sku || '—'}</td>}
                    <td>{item.item_name || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-m)' }}>{item.quantity}</td>
                    <td>{item.unit_of_measure || '—'}</td>
                    <td><span className="td-amt">${(parseFloat(item.unit_price) || 0).toFixed(2)}</span></td>
                    <td>
                      {hasDiscrepancy ? (
                        <span className="td-amt" style={{ color: 'var(--warning)' }} title={`Calculated: $${calculated.toFixed(2)} | Actual: $${actual.toFixed(2)}`}>
                          ${displayTotal.toFixed(2)} !
                        </span>
                      ) : (
                        <span className="td-amt">${displayTotal.toFixed(2)}</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--st500)' }}>{catLabel}</td>
                    <td>
                      <div className="settings-form__action-btns">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(item)}>Edit</button>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => removeLineItem(item.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Misc Charges — compact inline table */}
      {setMiscCharges && miscCharges.length > 0 && (
        <div style={{ marginTop: 'var(--sp3)' }}>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Charge</th>
                  <th scope="col">Amount</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {miscCharges.map(charge => (
                  <tr key={charge.id}>
                    <td>{charge.description || '—'}</td>
                    <td><span className="td-amt">${(parseFloat(charge.amount) || 0).toFixed(2)}</span></td>
                    <td>
                      <div className="settings-form__action-btns">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setChargeForm({ description: charge.description || '', amount: charge.amount || 0 }); setEditingCharge(charge); }}>Edit</button>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setMiscCharges(miscCharges.filter(c => c.id !== charge.id))}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Charge Modal */}
      {setMiscCharges && (
        <Modal
          isOpen={showAddChargeModal}
          onClose={() => setShowAddChargeModal(false)}
          title="Add Miscellaneous Charge"
          size="sm"
        >
          <div>
            <div className="field">
              <label className="label" htmlFor="charge-desc">Description <span className="req">*</span></label>
              <input id="charge-desc" className="input" type="text" value={chargeForm.description} onChange={e => setChargeForm({ ...chargeForm, description: e.target.value })} placeholder="e.g., Delivery Fee" />
            </div>
            <div className="field">
              <label className="label" htmlFor="charge-amount">Amount <span className="req">*</span></label>
              <input id="charge-amount" className="input" type="number" step="0.01" min="0" value={chargeForm.amount} onChange={e => setChargeForm({ ...chargeForm, amount: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
              <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                if (!chargeForm.description || !chargeForm.amount) return;
                const newId = miscCharges.length > 0 ? Math.max(...miscCharges.map(c => c.id)) + 1 : 0;
                setMiscCharges([...miscCharges, { id: newId, ...chargeForm }]);
                setShowAddChargeModal(false);
              }}>Add Charge</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowAddChargeModal(false)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Charge Modal */}
      {setMiscCharges && (
        <Modal
          isOpen={!!editingCharge}
          onClose={() => setEditingCharge(null)}
          title="Edit Charge"
          size="sm"
        >
          <div>
            <div className="field">
              <label className="label" htmlFor="edit-charge-desc">Description <span className="req">*</span></label>
              <input id="edit-charge-desc" className="input" type="text" value={chargeForm.description} onChange={e => setChargeForm({ ...chargeForm, description: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-charge-amount">Amount <span className="req">*</span></label>
              <input id="edit-charge-amount" className="input" type="number" step="0.01" min="0" value={chargeForm.amount} onChange={e => setChargeForm({ ...chargeForm, amount: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
              <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                if (!chargeForm.description || !chargeForm.amount) return;
                setMiscCharges(miscCharges.map(c => c.id === editingCharge.id ? { ...c, ...chargeForm } : c));
                setEditingCharge(null);
              }}>Save</button>
              <button type="button" className="btn btn-ghost" onClick={() => setEditingCharge(null)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Item Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title="Add Line Item"
        size="md"
      >
        <div>
          {renderFormFields('add')}
          <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={handleAdd}>Add Item</button>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Edit Item Modal */}
      <Modal
        isOpen={!!editingItem}
        onClose={() => { setEditingItem(null); resetForm(); }}
        title={`Edit Line Item #${editingItem ? lineItems.findIndex(i => i.id === editingItem.id) + 1 : ''}`}
        size="md"
      >
        <div>
          {renderFormFields('edit')}
          <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={handleEditSave}>Save</button>
            <button type="button" className="btn btn-ghost" onClick={() => { setEditingItem(null); resetForm(); }}>Cancel</button>
          </div>
        </div>
      </Modal>

      <QuickAddCategoryModal
        isOpen={showCategoryModal}
        onClose={() => { setShowCategoryModal(false); setActiveLineItemId(null); }}
        onCategoryCreated={handleCategoryCreated}
      />

      <QuickAddSKUModal
        isOpen={showSKUModal}
        onClose={() => { setShowSKUModal(false); setActiveLineItemId(null); }}
        onSKUCreated={handleSKUCreated}
        vendorId={vendorId}
        vendorName={vendorName}
        categories={categories}
      />
    </div>
  );
}

LineItemsTable.propTypes = {
  lineItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    sku: PropTypes.string,
    item_name: PropTypes.string,
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unit_of_measure: PropTypes.string,
    unit_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    actual_total: PropTypes.number,
  })).isRequired,
  setLineItems: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    full_path: PropTypes.string,
  })).isRequired,
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorName: PropTypes.string,
  onCategoryCreated: PropTypes.func,
  showSku: PropTypes.bool,
};

export default LineItemsTable;
